import { onAnimationEnd } from "@theme/utilities";
import { Component } from "@theme/component";
import { ensureGsap, prefersReducedMotion as prefersReducedMotion$1 } from "@theme/vp-motion";
import { StandardEvents } from "@shopify/events";
import { ThemeEvents } from "@theme/events";
import { formatMoney } from "@theme/money-formatting";
import { morph } from "@theme/morph";
//#region assets/sticky-add-to-cart.ts
/**
* @typedef {Object} ProductVariant
* @property {string|number} [id] - Variant ID
* @property {string} [title] - Variant title
* @property {string} [name] - Variant name
* @property {boolean} [available] - Whether variant is available
* @property {Object} [featured_media] - Featured media object
* @property {Object} [featured_media.preview_image] - Preview image data
* @property {string} [featured_media.preview_image.src] - Image source URL
* @property {string} [featured_media.alt] - Alt text for the image
*/
/**
* @typedef {HTMLElement & {
*   source: Element,
*   destination: Element,
*   useSourceSize: string | boolean
* }} FlyToCart
*/
/**
* @typedef {Object} StickyAddToCartRefs
* @property {HTMLElement} stickyBar - The floating bar container
* @property {HTMLButtonElement} addToCartButton - Sticky bar's button
* @property {HTMLElement} quantityDisplay - Quantity display container
* @property {HTMLElement} quantityNumber - Quantity number element
* @property {HTMLImageElement} productImage - Product image element
*/
/**
* A custom element that manages a sticky add-to-cart bar.
* Shows when the main buy buttons scroll out of view.
*
* @extends {Component<StickyAddToCartRefs>}
*/
var StickyAddToCartComponent = class extends Component {
	requiredRefs = [
		"stickyBar",
		"addToCartButton",
		"quantityDisplay",
		"quantityNumber"
	];
	#gsap = null;
	/** @type {IntersectionObserver | null} */
	#buyButtonsIntersectionObserver = null;
	/** @type {IntersectionObserver | null} */
	#mainBottomObserver = null;
	/** @type {number | undefined} */
	#resetTimeout;
	/** @type {boolean} */
	#isStuck = false;
	/** @type {number | null} */
	#animationTimeout = null;
	/** @type {AbortController} */
	#abortController = new AbortController();
	/** @type {HTMLButtonElement | null} */
	#targetAddToCartButton = null;
	/** @type {number} */
	#currentQuantity = 1;
	/** @type {boolean} */
	#hiddenByBottom = false;
	/** @type {ReturnType<typeof setInterval> | null} */
	#imageCarouselInterval = null;
	/** @type {number} */
	#imageCarouselIndex = 0;
	connectedCallback() {
		super.connectedCallback();
		this.#setupIntersectionObserver();
		this.#initImageCarousel();
		const { signal } = this.#abortController;
		this.closest(".shopify-section")?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
		document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
		document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
		document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
		this.#getInitialQuantity();
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this.#buyButtonsIntersectionObserver?.disconnect();
		this.#mainBottomObserver?.disconnect();
		this.#stopImageCarousel();
		this.#abortController.abort();
		if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
	}
	/**
	* Sets up the IntersectionObserver to watch the buy buttons visibility
	*/
	#setupIntersectionObserver() {
		const productForm = this.#getProductForm();
		if (!productForm) return;
		const buyButtonsBlock = productForm.closest(".buy-buttons-block") ?? productForm.querySelector(".product-details__actions") ?? productForm.querySelector(".product-details__sticky-bar") ?? productForm.querySelector(".product-details__stock-row");
		if (!buyButtonsBlock) return;
		const footer = document.querySelector("footer") ?? document.querySelector("[class*=\"footer-group\"]");
		if (!footer) return;
		this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
			const [entry] = entries;
			if (!entry) return;
			if (!entry.isIntersecting && !this.#isStuck) {
				const rect = entry.target.getBoundingClientRect();
				if (rect.bottom < 0 || rect.top < 0) this.#showStickyBar();
			} else if (entry.isIntersecting && this.#isStuck) {
				this.#hiddenByBottom = false;
				this.#hideStickyBar();
			}
		});
		this.#mainBottomObserver = new IntersectionObserver((entries) => {
			const [entry] = entries;
			if (!entry) return;
			if (entry.isIntersecting && this.#isStuck) {
				this.#hiddenByBottom = true;
				this.#hideStickyBar();
			} else if (!entry.isIntersecting && this.#hiddenByBottom) {
				const rect = buyButtonsBlock.getBoundingClientRect();
				if (rect.bottom < 0 || rect.top < 0) {
					this.#hiddenByBottom = false;
					this.#showStickyBar();
				}
			}
		}, { rootMargin: "200px 0px 0px 0px" });
		this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
		this.#mainBottomObserver.observe(footer);
		this.#targetAddToCartButton = productForm.querySelector("[ref=\"addToCartButton\"]");
	}
	/**
	* Handles the add to cart button click in the sticky bar
	*/
	handleAddToCartClick = async () => {
		if (!this.#targetAddToCartButton) return;
		if (this.#targetAddToCartButton.getAttribute("aria-busy") === "true") return;
		if (this.refs.addToCartButton?.getAttribute("aria-busy") === "true") return;
		this.#syncQuantityToProductForm();
		this.#targetAddToCartButton.dataset.puppet = "true";
		this.refs.addToCartButton?.setAttribute("aria-busy", "true");
		this.refs.addToCartButton && (this.refs.addToCartButton.disabled = true);
		this.#targetAddToCartButton.click();
		const cartIcon = document.querySelector(".header-actions__cart-icon") ?? document.querySelector("[data-vp-cart-toggle]");
		if (this.refs.addToCartButton.dataset.added !== "true") this.refs.addToCartButton.dataset.added = "true";
		const productImage = this.#getActiveProductImage();
		if (!cartIcon || !this.refs.addToCartButton || !productImage) return;
		if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
		const flyToCartElement = document.createElement("fly-to-cart");
		flyToCartElement.classList.add("fly-to-cart--sticky");
		flyToCartElement.style.setProperty("background-image", `url(${productImage.currentSrc || productImage.src})`);
		flyToCartElement.useSourceSize = "true";
		flyToCartElement.source = productImage;
		flyToCartElement.destination = cartIcon;
		document.body.appendChild(flyToCartElement);
		await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
		this.#resetTimeout = setTimeout(() => {
			this.refs.addToCartButton.removeAttribute("data-added");
		}, 800);
	};
	/**
	* Handles product select events (variant selected and updated)
	* @param {ProductSelectEvent} event - The product select event
	*/
	#handleProductSelect = (event) => {
		if (!(event.target instanceof Element) || event.target.closest("product-card")) return;
		const { optionValueId } = event.detail ?? {};
		if (optionValueId) this.dataset.currentVariantId = optionValueId;
		event.promise.then(({ detail }) => {
			if (!detail?.html) return;
			const { html, productId, resource: variant } = detail;
			if (productId && productId !== this.dataset.productId) return;
			const newStickyAddToCart = html.querySelector("sticky-add-to-cart");
			if (!newStickyAddToCart) return;
			const newStickyBar = newStickyAddToCart.querySelector("[ref=\"stickyBar\"]");
			if (!newStickyBar) return;
			const currentStuck = this.refs.stickyBar.getAttribute("data-stuck") || "false";
			const variantAvailable = newStickyAddToCart.dataset.variantAvailable;
			morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });
			this.refs.stickyBar.setAttribute("data-stuck", currentStuck);
			this.dataset.variantAvailable = variantAvailable;
			if (variant && variant.id) this.dataset.currentVariantId = variant.id;
			const productForm = this.#getProductForm();
			if (productForm) this.#targetAddToCartButton = productForm.querySelector("[ref=\"addToCartButton\"]");
			if (variant == null) this.#handleVariantUnavailable();
			this.#updateButtonText();
			this.#updateStickyPrice();
			this.#initImageCarousel();
			if (this.#isStuck) this.#startImageCarousel();
		}).catch((error) => {
			if (error?.name !== "AbortError") console.warn("[sticky-add-to-cart] Event promise rejected:", error);
		});
	};
	/**
	* Updates the variant title based on selected options when the variant is unavailable
	*/
	#handleVariantUnavailable = () => {
		this.dataset.currentVariantId = "";
		const variantTitleElement = this.querySelector(".vp-sticky-atc__subtitle, .sticky-add-to-cart__variant");
		const productId = this.dataset.productId;
		const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
		if (!variantTitleElement || !variantPicker) return;
		const selectedOptions = Array.from(variantPicker.querySelectorAll("input:checked")).map((option) => option.value).filter((value) => value !== "").join(" / ");
		if (!selectedOptions) return;
		variantTitleElement.textContent = selectedOptions;
	};
	/**
	* Handles cart add complete (success or error) - resets puppet flag
	* @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
	*/
	#handleCartAddComplete = (event) => {
		const resetPuppet = () => {
			if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = "false";
			if (this.refs.addToCartButton) {
				this.refs.addToCartButton.removeAttribute("aria-busy");
				this.refs.addToCartButton.disabled = this.dataset.variantAvailable === "false";
			}
		};
		if ("promise" in event && event.promise instanceof Promise) event.promise.finally(resetPuppet);
		else resetPuppet();
	};
	/**
	* Handles quantity selector update events
	* @param {QuantitySelectorUpdateEvent} event - The quantity update event
	*/
	#handleQuantityUpdate = (event) => {
		if (event.detail.cartLine) return;
		this.#currentQuantity = event.detail.quantity;
		this.#updateStickyPrice();
		const source = event.target;
		if (!source) return;
		const stickySelector = this.querySelector("quantity-selector-component");
		const formSelector = this.#getProductForm()?.querySelector("quantity-selector-component");
		if (!stickySelector || !formSelector) return;
		const stickyInput = stickySelector.querySelector("input[ref=\"quantityInput\"]");
		const formInput = formSelector.querySelector("input[ref=\"quantityInput\"]");
		if (!stickyInput || !formInput) return;
		const next = String(event.detail.quantity);
		if (stickySelector.contains(source) && formInput.value !== next) formInput.value = next;
		else if (formSelector.contains(source) && stickyInput.value !== next) stickyInput.value = next;
	};
	/**
	* Copies sticky quantity into the product form quantity input.
	*/
	#syncQuantityToProductForm() {
		const stickyInput = this.querySelector(".vp-sticky-atc__quantity input[ref=\"quantityInput\"], .sticky-add-to-cart__quantity input[ref=\"quantityInput\"]");
		const formInput = this.#getProductForm()?.querySelector("quantity-selector-component input[ref=\"quantityInput\"]");
		if (!stickyInput || !formInput) return;
		if (formInput.value !== stickyInput.value) formInput.value = stickyInput.value;
	}
	/**
	* Shows the sticky bar with animation
	*/
	#showStickyBar() {
		const { stickyBar } = this.refs;
		this.#isStuck = true;
		stickyBar.dataset.stuck = "true";
		this.#animateStickyBarVisibility(true);
		this.#startImageCarousel();
		this.#emitVisibility(true);
	}
	/**
	* Hides the sticky bar with animation
	*/
	#hideStickyBar() {
		const { stickyBar } = this.refs;
		this.#isStuck = false;
		stickyBar.dataset.stuck = "false";
		this.#animateStickyBarVisibility(false);
		this.#stopImageCarousel();
		this.#emitVisibility(false);
	}
	/**
	* Notify site chrome (e.g. sticky kenteken FAB, chat launcher) that the ATC bar toggled.
	* @param {boolean} stuck
	*/
	#emitVisibility(stuck) {
		const { stickyBar } = this.refs;
		window.dispatchEvent(new CustomEvent("vp-sticky-atc:visibility", { detail: {
			stuck,
			bar: stickyBar
		} }));
	}
	async #animateStickyBarVisibility(visible) {
		const { stickyBar } = this.refs;
		if (!stickyBar) return;
		if (prefersReducedMotion$1()) return;
		if (!this.#gsap) this.#gsap = await ensureGsap();
		const gsap = this.#gsap;
		if (!gsap) return;
		gsap.killTweensOf(stickyBar);
		if (visible) gsap.fromTo(stickyBar, { autoAlpha: 0 }, {
			autoAlpha: 1,
			duration: .25,
			ease: "power2.out"
		});
		else gsap.to(stickyBar, {
			autoAlpha: 0,
			duration: .2,
			ease: "power2.out"
		});
	}
	/**
	* Initializes sticky thumbnail carousel markup (active class on first image).
	*/
	#initImageCarousel() {
		const images = this.#getCarouselImages();
		if (images.length === 0) return;
		this.#imageCarouselIndex = 0;
		images.forEach((img, index) => {
			img.classList.toggle("is-active", index === 0);
		});
	}
	/**
	* @returns {HTMLImageElement[]}
	*/
	#getCarouselImages() {
		return Array.from(this.querySelectorAll(".vp-sticky-atc__image--carousel .vp-sticky-atc__image-img"));
	}
	/**
	* @returns {HTMLImageElement | null}
	*/
	#getActiveProductImage() {
		return this.querySelector(".vp-sticky-atc__image-img.is-active") ?? this.querySelector(".vp-sticky-atc__image-img") ?? this.refs.productImage ?? null;
	}
	#startImageCarousel() {
		this.#stopImageCarousel();
		const carousel = this.querySelector("[data-vp-sticky-image-carousel]");
		const images = this.#getCarouselImages();
		if (!carousel || images.length < 2) return;
		if (prefersReducedMotion$1()) return;
		const intervalMs = Number(carousel.getAttribute("data-interval")) || 2e3;
		this.#imageCarouselInterval = setInterval(() => {
			const slides = this.#getCarouselImages();
			if (slides.length < 2) {
				this.#stopImageCarousel();
				return;
			}
			slides[this.#imageCarouselIndex]?.classList.remove("is-active");
			this.#imageCarouselIndex = (this.#imageCarouselIndex + 1) % slides.length;
			slides[this.#imageCarouselIndex]?.classList.add("is-active");
		}, intervalMs);
	}
	#stopImageCarousel() {
		if (this.#imageCarouselInterval != null) {
			clearInterval(this.#imageCarouselInterval);
			this.#imageCarouselInterval = null;
		}
	}
	/**
	* Gets the product form element
	* @returns {HTMLElement | null}
	*/
	#getProductForm() {
		const productId = this.dataset.productId;
		if (!productId) return null;
		const sectionElement = this.closest(".shopify-section");
		if (!sectionElement) return null;
		const sectionId = sectionElement.id.replace("shopify-section-", "");
		return document.querySelector(`#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`);
	}
	/**
	* Gets the initial quantity from the data attribute
	*/
	#getInitialQuantity() {
		this.#currentQuantity = parseInt(this.dataset.initialQuantity || "1") || 1;
		this.#updateStickyPrice();
	}
	/**
	* Updates sticky bar total price from unit price × quantity.
	*/
	#updateStickyPrice() {
		const priceRoot = this.querySelector(".vp-sticky-atc__price");
		if (!(priceRoot instanceof HTMLElement)) return;
		const unitPrice = Number(priceRoot.dataset.unitPrice || 0);
		const unitCompare = Number(priceRoot.dataset.unitComparePrice || 0);
		const moneyFormat = priceRoot.dataset.moneyFormat || "{{amount}}";
		const currency = priceRoot.dataset.currency || "EUR";
		const qty = Math.max(1, this.#currentQuantity || 1);
		const currentEl = priceRoot.querySelector(".vp-sticky-atc__price-current");
		const compareEl = priceRoot.querySelector(".vp-sticky-atc__price-compare");
		if (currentEl) currentEl.textContent = formatMoney(unitPrice * qty, moneyFormat, currency);
		if (compareEl && unitCompare > unitPrice) compareEl.textContent = formatMoney(unitCompare * qty, moneyFormat, currency);
	}
	/**
	* Legacy quantity badge helper — kept for Horizon markup compatibility.
	* Poelgeest sticky bar uses a real quantity selector, so this stays hidden.
	*/
	#updateButtonText() {
		const { quantityDisplay, quantityNumber } = this.refs;
		if (!quantityDisplay || !quantityNumber) return;
		quantityNumber.textContent = this.#currentQuantity.toString();
		quantityDisplay.style.display = "none";
	}
};
if (!customElements.get("sticky-add-to-cart")) customElements.define("sticky-add-to-cart", StickyAddToCartComponent);
//#endregion
